import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Icon } from "./Icons";
import { Modal } from "./Modal";

export type Command = { id: string; label: string; hint?: string; run: () => void };

/** ⌘K / Ctrl+K: jump anywhere or run an action without leaving the keyboard. */
export function CommandMenu({ extra = [] }: { extra?: Command[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  const commands = useMemo<Command[]>(
    () => [
      { id: "pages", label: "Go to Pages", hint: "G P", run: () => navigate({ to: "/pages" }) },
      { id: "works", label: "Go to Works", hint: "G W", run: () => navigate({ to: "/works" }) },
      { id: "media", label: "Go to Media", hint: "G M", run: () => navigate({ to: "/media" }) },
      { id: "people", label: "Go to People", run: () => navigate({ to: "/people" }) },
      { id: "inbox", label: "Go to Inbox", hint: "G I", run: () => navigate({ to: "/inbox" }) },
      { id: "admin", label: "Go to Admin (team, connections, activity, system health)", run: () => navigate({ to: "/admin/team" }) },
      ...extra,
    ],
    [navigate, extra],
  );
  const q = query.trim().toLowerCase();
  // With no query, show actions only; typing searches works, people and pages too.
  const results = commands.filter((c) => (q ? c.label.toLowerCase().includes(q) : !c.label.includes(": "))).slice(0, 12);

  useEffect(() => {
    let pendingG = false,
      timer: number | undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      // Two-key shortcuts ("g" then a letter) when not typing in a field.
      const typing = e.target instanceof HTMLElement && e.target.closest("input, textarea, select, [contenteditable]");
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (pendingG) {
        pendingG = false;
        // Lower-cased, so Caps Lock or Shift don't break them.
        const to = ({ p: "/pages", w: "/works", m: "/media", i: "/inbox" } as const)[e.key.toLowerCase() as "p"];
        if (to) navigate({ to });
      } else if (e.key.toLowerCase() === "g") {
        pendingG = true;
        clearTimeout(timer);
        timer = window.setTimeout(() => (pendingG = false), 800);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // Focus moves into the menu and back out through Modal.
  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
    }
  }, [open]);

  const run = (c: Command | undefined) => {
    if (!c) return;
    setOpen(false);
    c.run();
  };

  return (
    <>
      {/* Always the same element, so focus can return to it when the menu closes. */}
      <OpenButton onOpen={() => setOpen(true)} />
      {open && (
        <Modal className="palette" label="Command menu" onClose={() => setOpen(false)}>
          <input
            value={query}
            placeholder="Type a command or search…"
            aria-label="Command"
            aria-controls="cmd-results"
            aria-activedescendant={results[index] ? `cmd-${results[index].id}` : undefined}
            onChange={(e) => {
              setQuery(e.target.value);
              setIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setIndex((i) => Math.min(i + 1, results.length - 1));
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setIndex((i) => Math.max(i - 1, 0));
              }
              if (e.key === "Enter") run(results[index]);
            }}
          />
          {/* The listbox pattern with aria-activedescendant: focus stays in the input, arrows move the selection. */}
          {/* biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: see above */}
          <ul id="cmd-results" role="listbox">
            {results.map((c, i) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/useFocusableInteractive lint/a11y/noNoninteractiveElementToInteractiveRole: options are chosen from the input with the arrow keys and Enter
              <li key={c.id} id={`cmd-${c.id}`} role="option" aria-selected={i === index} onMouseEnter={() => setIndex(i)} onClick={() => run(c)}>
                <span>{c.label}</span>
                {c.hint && <kbd>{c.hint}</kbd>}
              </li>
            ))}
            {!results.length && <li className="none">No matching commands</li>}
          </ul>
        </Modal>
      )}
    </>
  );
}

function OpenButton({ onOpen }: { onOpen: () => void }) {
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);
  return (
    <button type="button" className="cmd-open" onClick={onOpen} aria-label="Search or jump to" title="Search or jump to">
      <Icon name="search" className="cmd-icon" />
      <span>Search or jump to…</span>
      <kbd>{mac ? "⌘K" : "Ctrl K"}</kbd>
    </button>
  );
}
